import { Card } from '@/components/ui/card';
import { t, getLocale } from '@/i18n';
import { UrgencySavings } from './UrgencySavings';

const fmt = (n, digits = 0) => (n == null ? '—' : n.toLocaleString(getLocale(), { maximumFractionDigits: digits }));

const Tile = ({ label, value, unit, hint }) => (
  <Card className="p-4">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold mt-1 tabular-nums">
      {value}<span className="text-sm font-medium text-muted-foreground"> {unit}</span>
    </div>
    {hint && <div className="text-xs mt-1 text-muted-foreground">{hint}</div>}
  </Card>
);

// Reply rate as a small bar next to the number.
const ReplyBar = ({ pct }) => (
  <span className="inline-block w-16 h-1.5 rounded-full bg-muted overflow-hidden align-middle" aria-hidden="true">
    <span className="block h-full rounded-full bg-chart-1" style={{ width: `${pct}%` }} />
  </span>
);

// Follow-up statistics of the pilot: the savings estimate first, then KPIs and channels.
export const UrgencyStats = ({ summary }) => (
  <div className="space-y-6">
    <UrgencySavings summary={summary} />

    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">
        {t('stats.urgency.period').replace('{days}', summary.pilotDays).replace('{total}', summary.total)}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label={t('stats.urgency.replyPct')} value={fmt(summary.replyPct)} unit="%" />
        <Tile label={t('stats.urgency.replyMedian')} value={fmt(summary.replyMedian)} unit="min" />
        <Tile label={t('stats.urgency.escalated')} value={fmt(summary.escalatedPct)} unit="%" hint={t('stats.urgency.escalatedHint')} />
        <Tile
          label={t('stats.urgency.resolution')}
          value={`${fmt(summary.autoResolved)} / ${fmt(summary.toPlanner)}`}
          hint={t('stats.urgency.resolutionHint')}
        />
      </div>
    </div>

    <Card className="p-0 overflow-hidden">
      <div className="px-4 pt-3 pb-2 text-sm font-semibold">{t('stats.urgency.byChannel')}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-4 py-2">{t('stats.urgency.channel')}</th>
              <th className="text-right font-medium px-3 py-2">{t('stats.urgency.sent')}</th>
              <th className="text-right font-medium px-3 py-2">{t('stats.urgency.replyPct')}</th>
              <th className="text-right font-medium px-3 py-2">{t('stats.urgency.replyMedian')}</th>
              <th className="text-right font-medium px-4 py-2">{t('stats.urgency.cost')}</th>
            </tr>
          </thead>
          <tbody>
            {summary.channels.map(c => (
              <tr key={c.id} className="border-t">
                <td className="px-4 py-2.5 font-medium whitespace-nowrap">{t(`arrivals.channel.${c.id}`)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmt(c.sent)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                  {c.replyPct != null && <ReplyBar pct={c.replyPct} />}
                  <span className="inline-block min-w-[3.5rem]">{fmt(c.replyPct)}{c.replyPct != null && ' %'}</span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmt(c.replyMedian)}{c.replyMedian != null && ' min'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{fmt(c.costCzk)} {t('stats.urgency.czk')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] text-muted-foreground border-t">{t('stats.urgency.channelLegend')}</div>
    </Card>
  </div>
);
